<template>
  <v-container fluid class="px-6 py-0">
    <v-row class="nmb-2">
      <v-form class="full-width form-container">
        <div class="backdrop">
          <div class="v-input v-textarea v-textarea--auto-grow v-textarea--no-resize v-input--is-label-active v-input--is-dirty v-text-field v-text-field--single-line v-text-field--solo v-text-field--solo-flat v-text-field--is-booted v-text-field--enclosed">
            <div class="v-input__control">
              <div class="v-input__slot">
                <div class="v-text-field__slot">
                  <div
                    class="highlights source-input"
                    v-on:click="clickInput"
                    id="source-input"
                    v-html="predicted || inputRaw">
                  </div>
                </div>
                <div class="v-input__append-inner clickable" @click="resetInput">
                  <div class="v-input__icon v-input__icon--clear" :class="inputRaw ? 'visible' : ''">
                    <v-btn icon>
                      <button type="button" aria-label="clear icon" class="v-icon">
                        <iconify-icon class="iconify" data-icon="mdi-close"></iconify-icon>
                      </button>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <v-textarea
          @focus='isTyping = true'
          @blur='isTyping = false'
          class='input-textarea'
          solo
          flat
          no-resize
          auto-grow
          clearable
          :error="appInvalidInput"
          :error-messages="activeErrorMsg"
          @click:clear="resetInput"
          :value="inputRaw"
          @input='handleInput'
          v-on:input="writing"
          v-on:paste="pasting"
          v-debounce:300ms='handleInput'
          :autofocus="false"
          :placeholder="placeholder"
          rows="7"
          name="input-9-5"
          label=""
          >
        </v-textarea>
        </v-form>
    </v-row>
    <v-row align="center" class="px-4 my-0 py-2">
      <span v-if="mismatch" class='suggested-language'>
        <v-btn text disabled>{{ $t('suggested_lang_label') }}:</v-btn>
        <v-btn @click="setSuggestedLanguage" color="primary" text link>{{ suggestedLanguage }}</v-btn>
      </span>
      <span v-if="mispelledConfirmation" >
        <v-btn text disabled>{{ $t('do_you_mean_label') }}:</v-btn>
        <v-btn @click="setSuggestedSpelling" class="h-auto" v-html="htmlSpelling" color="primary" text link></v-btn>
      </span>
    </v-row>
  </v-container>
</template>

<script>
  import { ref, inject, computed, watch } from 'vue'
  import { getCurrentInstance } from 'vue'
  import { createMapLangCodeToName } from '../../utils/language'
  import { requestIpaInfo } from '../../utils/client';
  import { debounce } from '../../utils/events';
  import { useEvent } from '../../plugins/vue-gtag';
  import { inflateTemplate } from '../../utils/wordParsing';
  
  const typeDebounceTiming = 450;

  export default {
    events: ['ipa','suggested-language'],
    props: {
      maxSizeAllow: {
        type: Number,
        default: 2000
      },
      locale: {
        type: String,
        required: true
      },
      langCode: {
        type: String,
        required: true
      }
    },
    setup(props, context){
      const root = getCurrentInstance();

      let recognitionEnabled = false; // ref(!!$window['webkitSpeechRecognition']);
      const isTyping = inject('isTyping');
      let playing = inject('playing');
      let language = inject('language');
      let ttsLocale = inject('ttsLocale');
      let ttsMode = inject('ttsMode');
      let plainWords = inject('plainWords');
      let inputRaw = inject('appTTS');
      let snackbarMessage = inject('snackbarMessage');
      let ttsIndex = inject('ttsIndex');
      let appInvalidInput = inject('appInvalidInput');
      let correctSpelling = ref('');
      let detectedCode = ref(null);
      let mispelledConfirmation = ref(false);
      let appMaxSize = inject('appMaxSize');
      let useIpa = inject('useIpa');
      let core = inject('core');
      let speed = inject('speed');
      let voiceId = inject('voiceId');
      const gaClearText = useEvent('clear_text');
      const gaPasteText = useEvent('paste_text');
      const gaTyping = debounce(useEvent('typing'), typeDebounceTiming);

      const shouldUseIpa = (detected)=>{
        useIpa.value = detected !== 'id' && detected != language.value;
      }

      appMaxSize.value = props.maxSizeAllow;
      let translateCallback = (response) => {
        const detected = response.data.detected;
        detectedCode.value = (detected && detected !== 'UNKNOWN') ? detected : null;
        if(response.data.spelling){
          correctSpelling.value = response.data.spelling;
        }
        shouldUseIpa(detectedCode.value);
        context.emit('ipa',response.data.ipa);
        context.emit('loading', false);
      };
      let translate = debounce(function(){
        context.emit('loading', true);
        if(inputRaw.value === ""){
          context.emit('ipa', null);
        }
        if (!appInvalidInput.value){
          requestIpaInfo(inputRaw.value, ttsLocale.value || props.langCode, {
            mode: ttsMode.value
          })
          .then(translateCallback)
          .catch((error) => {
            console.log(error.response.data);
            context.emit('loading', false);
          });
        }
      }, typeDebounceTiming);
      let clickInput = function(e){
        if(e.target.matches('.clickable')){
          context.emit('click', e);
        }
      };
      let inflateTemplateFn = function(markIndex, textId, speed){
        const audioMap = textId && core?.value?.audiosMaps ? core?.value?.audiosMaps[textId] : null;
        return inflateTemplate(inputRaw.value, markIndex, ttsMode.value, '', core?.value?.currentTime?.value, audioMap, speed);
      };
      const writing = function(){
        context.emit('loading', true);
      };
      const pasting = function(){
        gaPasteText();
        console.log('paste event detected');
      };

      const ltrim = function(str) {
        if(!str) return str;
        return str.replace(/^\s+/g, '');
      }

      watch(inputRaw, ()=>{
        translate();
      });

      watch(ttsMode, ()=>{
        translate();
      });
      const resetInput = function() {
        gaClearText();
        inputRaw.value = '';
        correctSpelling.value = '';
        detectedCode.value = null; 
        context.emit('ipa','');
        context.emit('stopSpeech');
        setTimeout(() => {
          context.emit('loading', false);
        },100);
      };
      let setSuggestedLanguage = function() {
        context.emit('suggested-language', detectedCode.value);
      };
      let setSuggestedSpelling = function() {
        inputRaw.value = correctSpelling.value;
        mispelledConfirmation.value = false
        translate();
      };
      // computed methods
      let placeholder = computed(function(){
        return root.proxy.$i18n.t('placeholder');
      });
      let predicted = computed(function() {
        if(!plainWords.value){
          return null;
        }
        if(!playing.value){
          return inflateTemplateFn();
        }
        let trackingIndex = ttsIndex.value;
        const word = plainWords.value[trackingIndex];
        const textId = word.trim() + speed.value + voiceId.value;
        return inflateTemplateFn(trackingIndex, textId, speed.value);
      });
      let mispelling = computed(function() {
        if(inputRaw.value && inputRaw.value.length > 0 && inputRaw.value.localeCompare(correctSpelling.value) !== 0){
          return true;
        }
        return false;
      });
      let mismatch = computed(function() {
        if(!detectedCode.value){
          return false;
        }
        if(detectedCode.value){
          return detectedCode.value !== props.langCode;
        } else {
          return false;
        }
      });
      let htmlSpelling = computed(function() {
        return `<span class="correct-spelling">${correctSpelling.value}</span>`;
      });
      let suggestedLanguage = computed(function() {
        return createMapLangCodeToName(this.locale)[detectedCode.value]
      });
      // watch methods
      watch(inputRaw, function(newValue){
        correctSpelling.value = inputRaw.value;
        if(newValue && newValue.length > props.maxSizeAllow){
          appInvalidInput.value = true;
          // this.activeErrorMsg = this.errorMsg;
        } else {
          appInvalidInput.value = false;
          // this.activeErrorMsg = '';
        }
      });
      watch(()=> props.langCode, () => {
        translate();
      });
      watch(ttsLocale, () => {
        translate();
      });
      watch(correctSpelling, function(){
        setTimeout(() => {
          if(mispelling.value && inputRaw.value.localeCompare(correctSpelling.value) !== 0){
            mispelledConfirmation.value = true
          } else {
            mispelledConfirmation.value = false
          }
        },300)

      });
      setTimeout(() => {
        if(inputRaw.value){
          translate();
        }
      }, 100)

      const handleInput = (text)=>{
        if (text){
          gaTyping();
          const trimmed = ltrim(text);
          if (text.length < appMaxSize.value){
            inputRaw.value = trimmed;
          } else {
            snackbarMessage.value = 'Text is longer than 2000 characters!'
            inputRaw.value = trimmed.substr(0, appMaxSize.value);
          }
        } else {
          inputRaw.value = text;
        }
      }

      return {
        recognitionEnabled,
        inputRaw,
        writing,
        pasting,
        placeholder,
        detectedCode,
        appInvalidInput,
        mispelledConfirmation,
        mispelling,
        correctSpelling,
        resetInput,
        predicted,
        mismatch,
        setSuggestedLanguage,
        setSuggestedSpelling,
        suggestedLanguage,
        translate,
        htmlSpelling,
        clickInput,
        isTyping,
        handleInput
      }
    },
    data: function() {
      return {
        activeErrorMsg: '',
        errorMsg: this.$t('validations.max_input',{maxSizeAllow: this.maxSizeAllow}),
      }
    },
  }
</script>

<style scoped lang="scss">
  .input-textarea.v-text-field--solo ::v-deep > .v-input__control > .v-input__slot {
    transition: none;
  }
  .error--text, .theme--light.v-input input, .theme--light.v-input.error--text textarea {
    color: #ff5252;
  }
  .v-messages.error--text .v-messages__message {
    font-size: small;
  }
  .v-textarea.v-text-field.v-text-field--solo .v-input__append-inner, .v-textarea.v-text-field.v-text-field--solo .v-input__prepend-inner {
    align-self: flex-start;
    margin-top: 1rem;
  }
  .v-btn .v-btn__content {
    text-transform: none;
  }
  .v-btn::before {
    background-color: transparent;
  }
  .v-btn.v-btn--disabled:not(.v-btn--round).v-size--default {
    padding: 0;
  }
  .v-btn.v-btn--disabled::before {
    content: none;
  }
  .nmb-2 {
    margin-bottom: -2rem;
  }
  #source-input {
    padding-right: 12px;
    margin-top: 10px;
    line-height: 28px;
    white-space: pre-wrap;
    word-spacing: 0px;
    writing-mode: horizontal-tb;
    text-size-adjust: 100%;
    tab-size: 4;
    text-align: start;
    text-rendering: auto;
    overflow-wrap: break-word;
    max-width: 100%;
    letter-spacing: normal;
    font-stretch: 100%;
    font-size: 20px;
    font-weight: 400;
    box-sizing: border-box;
    align-self: stretch;
  }

  #source-input.text-indent-0 {
    text-indent: 0px;
  }
  #source-input.text-indent-5 {
    text-indent: 0px;
  }

  textarea {
    margin: 0;
    border-radius: 0;
    background-color: transparent;
    position: absolute;
    z-index: 2;
  }
  .backdrop {
    width: 100%;
    position: absolute;
    pointer-events: none;
    z-index: 1;
    overflow: auto;
    transition: transform 1s;
  }

  .backdrop .v-input__control, .backdrop .v-input__slot, .backdrop .v-text-field__slot {
    background-color: transparent !important;
  }
  .visible {
    visibility: visible;
    opacity: 1;
  }
  .highlights {
    white-space: pre-wrap;
    word-wrap: break-word;
    color: transparent;
  }
  .form-container{
    display: block;
    margin: 0 auto;
    transform: translateZ(0);
    -webkit-text-size-adjust: none;
  }
  .clickable {
    pointer-events: all;
    cursor: pointer;
  }
  .source-input ::v-deep *{
    font-family: Roboto, sans-serif;
  }
  .source-input ::v-deep pre {
    display: inline-block;
    word-spacing: 0;
  }
  .source-input ::v-deep span {
    color: transparent;
  }
</style>
